import { useCallback, useMemo, useState } from 'react'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import {
  buildErrorGroups,
  buildErrorRowIndex,
  type ParsedRow,
} from './bulkUpload.helpers'

/** Preview rows plus the validation derived from them, re-run on every edit so
 *  a row clears its error as soon as it is fixed. */
export const useBulkUploadRows = () => {
  const [rows, setRows] = useState<ParsedRow[]>([])
  const { data: clients = [] } = useMyClientsQuery()

  const clientNameSet = useMemo(
    () => new Set(clients.map((c) => c.name.toLowerCase())),
    [clients]
  )

  const isKnownClientName = useCallback(
    (name: string) => clientNameSet.has(name.toLowerCase()),
    [clientNameSet]
  )

  const displayRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        clientMatch:
          row.clientName === '' ? false : isKnownClientName(row.clientName),
      })),
    [rows, isKnownClientName]
  )

  const errorGroups = useMemo(
    () => buildErrorGroups(displayRows),
    [displayRows]
  )
  const errorIndex = useMemo(
    () => buildErrorRowIndex(errorGroups),
    [errorGroups]
  )

  const handleClientFix = useCallback(
    (rowNum: number, clientName: string) =>
      setRows((curr) =>
        curr.map((row) =>
          row.rowNum === rowNum
            ? { ...row, clientName, clientMatch: true }
            : row
        )
      ),
    []
  )

  const handleFieldChange = useCallback(
    (rowNum: number, field: keyof ParsedRow, value: string) =>
      setRows((curr) =>
        curr.map((row) =>
          row.rowNum === rowNum ? { ...row, [field]: value } : row
        )
      ),
    []
  )

  return {
    rows,
    setRows,
    clients,
    isKnownClientName,
    displayRows,
    errorGroups,
    errorIndex,
    handleClientFix,
    handleFieldChange,
  }
}
